/**
 * Checkout Operations - Saleor
 *
 * Saleor queries and mutations for checkout functionality.
 *
 * Operations in this file:
 * - checkoutRefresh: Get latest checkout data
 * - checkoutRestore: Restore checkout from storage
 * - checkoutLinesUpdate: Update line quantities
 * - checkoutEmailUpdate: Update customer email
 * - checkoutBillingAddressUpdate: Update billing address
 * - orderUpdate: Update order fields (e.g. shipping address for express checkout)
 *
 * Note: Most mutations go through OrderApp (see orderapp.ts).
 * Saleor is used for queries and simple updates that don't need
 * Breitling business logic.
 */

import {
  CheckoutBillingAddressUpdateDocument,
  CheckoutCustomerAttachDocument,
  CheckoutEmailUpdateDocument,
  CheckoutLinesUpdateDocument,
  GetCheckoutDocument,
  OrderUpdateDocument,
  UserCheckoutsDocument,
  type SaleorLanguageCodeEnum,
  type SaleorOrderFragment,
  type SaleorOrderUpdateMutationVariables,
  type SaleorUserCheckoutsQuery,
  type SaleorUserCheckoutsQueryVariables,
} from '@/graphql/saleor/api';
import { BRAND } from '@/lib/const';
import { saleorMutate, saleorQuery } from '@/lib/graphql/client';
import { devError } from '@/lib/utils/devLog';
import type { ApiError, CheckoutData } from '../types';
import {
  ensureCheckoutSessionCookie,
  getClientPathLocale,
  isCheckoutBrowserClient,
  postCheckoutBff,
} from './checkoutBffClient';
import {
  DEFAULT_LANGUAGE_CODE,
  IMAGES_FORMAT,
  mapApiError,
  mapCheckoutToData,
  mapToSaleorAddress,
  THUMBNAIL_SIZE,
  type CheckoutAddressPayload,
} from './types';

/**
 * User checkout summary (minimal data for restoration)
 */
export interface UserCheckoutSummary {
  id: string;
  created: string;
  updatedAt: string;
  channelSlug: string;
  lineCount: number;
}

// ============================================
// Queries
// ============================================

/**
 * Get user's checkouts by email and channel
 *
 * Used to restore a user's checkout after login when the checkout cookie
 * is missing (e.g., cleared cookies, new device).
 *
 * IMPORTANT: This query requires the user's access token because:
 * - Querying checkouts with filters requires MANAGE_CHECKOUTS permission
 * - OR the authenticated user can query their own checkouts
 *
 * @param userEmail - User's email address
 * @param channel - Channel slug (e.g., 'us-usd')
 * @param accessToken - User's access token for authenticated query
 * @returns List of checkout summaries, most recent first
 */
export async function getUserCheckouts(params: { userEmail: string; channel: string }): Promise<{
  checkouts: UserCheckoutSummary[];
  error?: string;
}> {
  try {
    const result = await saleorQuery<SaleorUserCheckoutsQuery, SaleorUserCheckoutsQueryVariables>(
      UserCheckoutsDocument,
      {
        userEmail: params.userEmail,
        channel: params.channel,
      }
    );

    const checkouts: UserCheckoutSummary[] = (result.checkouts?.edges ?? [])
      .map((edge) => edge.node)
      .filter((node) => node.lines.length > 0)
      .filter((node) => node.orderBrand === BRAND)
      .map((node) => ({
        id: node.id,
        created: node.created,
        updatedAt: node.updatedAt,
        channelSlug: node.channel.slug,
        lineCount: node.lines.reduce((sum, line) => sum + line.quantity, 0),
      }));

    return { checkouts };
  } catch (error) {
    devError('[Checkout] Failed to fetch user checkouts:', error);
    return {
      checkouts: [],
      error: error instanceof Error ? error.message : 'Failed to fetch checkouts',
    };
  }
}

/**
 * Restore checkout from storage or API
 *
 * IMPORTANT: Like checkoutRefresh, this uses the BFF endpoint on client-side
 * because Saleor checkout queries require admin-level permissions.
 */
export async function checkoutRestore(params: { id?: string; languageCode?: string }): Promise<{
  checkout: CheckoutData | null;
  isExpired: boolean;
  orderId?: string;
  paymentMethod?: unknown;
  isChangedAfterOrderCreated?: boolean;
}> {
  if (!params.id) {
    return {
      checkout: null,
      isExpired: false,
    };
  }

  // Use checkoutRefresh which handles client/server detection
  const result = await checkoutRefresh({
    id: params.id,
    languageCode: params.languageCode,
  });

  return {
    checkout: result.checkout,
    isExpired: result.isExpired ?? !result.checkout,
  };
}

/**
 * Refresh checkout data from server
 *
 * IMPORTANT: On client-side, this calls the BFF endpoint (/api/checkout/refresh)
 * because Saleor checkout queries require admin-level permissions (MANAGE_USERS,
 * HANDLE_PAYMENTS, etc.) that user tokens don't have. The BFF endpoint uses
 * the server-side SALEOR_APP_TOKEN which has these permissions.
 *
 * On server-side, this calls Saleor directly using the app token.
 */
export async function checkoutRefresh(params: {
  id: string;
  languageCode?: string;
  channelSlug?: string;
  full?: boolean;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
  isExpired?: boolean;
}> {
  // Client-side: use BFF endpoint
  if (isCheckoutBrowserClient()) {
    return checkoutRefreshViaApi(params);
  }

  // Server-side: call Saleor directly (has app token)
  return checkoutRefreshDirect(params);
}

/**
 * Client-side refresh via BFF endpoint
 */
async function checkoutRefreshViaApi(params: {
  id: string;
  languageCode?: string;
  channelSlug?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
  isExpired?: boolean;
}> {
  ensureCheckoutSessionCookie(params.id, params.channelSlug);

  return postCheckoutBff(
    '/api/checkout/refresh',
    {
      checkoutId: params.id,
      languageCode: params.languageCode,
      locale: getClientPathLocale(),
    },
    { code: 'REFRESH_FAILED', message: 'Failed to refresh checkout' },
    '[Checkout] Failed to refresh checkout via API:'
  );
}

/**
 * Server-side refresh directly via Saleor (uses app token)
 */
async function checkoutRefreshDirect(params: { id: string; languageCode?: string }): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
  isExpired?: boolean;
}> {
  try {
    const languageCode = (params.languageCode?.toUpperCase().replace('-', '_') ||
      DEFAULT_LANGUAGE_CODE) as SaleorLanguageCodeEnum;

    const result = await saleorQuery(GetCheckoutDocument, {
      id: params.id,
      languageCode,
      thumbnailSize: THUMBNAIL_SIZE,
      imagesFormat: IMAGES_FORMAT,
    });

    if (!result.checkout) {
      return { checkout: null, errors: [], isExpired: true };
    }

    return { checkout: mapCheckoutToData(result.checkout), errors: [] };
  } catch (error) {
    devError('[Checkout] Failed to refresh checkout:', error);
    return {
      checkout: null,
      errors: [{ code: 'REFRESH_FAILED', message: 'Failed to refresh checkout' }],
    };
  }
}

// ============================================
// Mutations
// ============================================

/**
 * Update line quantities in checkout
 * Note: Using Saleor directly as OrderApp doesn't have linesUpdate
 * TODO: Check if this should go through OrderApp
 */
export async function checkoutLinesUpdate(params: {
  id: string;
  lines: Array<{ lineId: string; quantity: number }>;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  try {
    const languageCode = (params.languageCode?.toUpperCase().replace('-', '_') ||
      DEFAULT_LANGUAGE_CODE) as SaleorLanguageCodeEnum;

    const result = await saleorMutate(CheckoutLinesUpdateDocument, {
      id: params.id,
      lines: params.lines,
      languageCode,
      thumbnailSize: THUMBNAIL_SIZE,
      imagesFormat: IMAGES_FORMAT,
    });

    const errors = result.checkoutLinesUpdate?.errors?.map(mapApiError) ?? [];
    const checkout = result.checkoutLinesUpdate?.checkout
      ? mapCheckoutToData(result.checkoutLinesUpdate.checkout)
      : null;

    return { checkout, errors };
  } catch (error) {
    devError('[Checkout] Failed to update lines:', error);
    return {
      checkout: null,
      errors: [
        {
          code: 'LINES_UPDATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update cart',
        },
      ],
    };
  }
}

/**
 * Update checkout email.
 *
 * Once the checkout has been attached to a logged-in customer, the
 * `Checkout` fragment returned by `checkoutEmailUpdate` includes `user`,
 * which Saleor only resolves with app-level permissions. The browser
 * doesn't have `SALEOR_APP_TOKEN` (intentionally — it's server-side only),
 * so on the client we route through the BFF (`/api/checkout/email`) which
 * signs the call server-side with the App token. Same pattern as
 * `checkoutBillingAddressUpdate` / `checkoutRefresh`.
 */
export async function checkoutEmailUpdate(params: {
  id: string;
  email: string;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  if (isCheckoutBrowserClient()) {
    return checkoutEmailUpdateViaApi(params);
  }
  return checkoutEmailUpdateDirect(params);
}

/**
 * Client-side email update — proxies through the BFF so the App token (which
 * lives only in server env) is the one that authenticates with Saleor.
 */
async function checkoutEmailUpdateViaApi(params: {
  id: string;
  email: string;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  return postCheckoutBff(
    '/api/checkout/email',
    {
      checkoutId: params.id,
      email: params.email,
      languageCode: params.languageCode,
    },
    { code: 'EMAIL_UPDATE_FAILED', message: 'Failed to update email' },
    '[Checkout] Failed to update email via API:'
  );
}

/**
 * Server-side email update — direct Saleor call using the App token.
 */
async function checkoutEmailUpdateDirect(params: {
  id: string;
  email: string;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  try {
    const languageCode = (params.languageCode?.toUpperCase().replace('-', '_') ||
      DEFAULT_LANGUAGE_CODE) as SaleorLanguageCodeEnum;

    const result = await saleorMutate(CheckoutEmailUpdateDocument, {
      id: params.id,
      email: params.email,
      languageCode,
      thumbnailSize: THUMBNAIL_SIZE,
      imagesFormat: IMAGES_FORMAT,
    });

    const errors = result.checkoutEmailUpdate?.errors?.map(mapApiError) ?? [];
    const checkout = result.checkoutEmailUpdate?.checkout
      ? mapCheckoutToData(result.checkoutEmailUpdate.checkout)
      : null;

    return { checkout, errors };
  } catch (error) {
    devError('[Checkout] Failed to update email:', error);
    return {
      checkout: null,
      errors: [
        {
          code: 'EMAIL_UPDATE_FAILED',
          message: 'Failed to update email',
        },
      ],
    };
  }
}

/**
 * Update billing address.
 *
 * Once the checkout has been attached to a logged-in customer Saleor's
 * `checkoutBillingAddressUpdate` requires admin-level permissions
 * (MANAGE_USERS / HANDLE_PAYMENTS / HANDLE_TAXES / OWNER) that the user's
 * own token doesn't carry. The browser also doesn't have `SALEOR_APP_TOKEN`
 * (intentionally — it's server-side only). So on the client we route through
 * the BFF (`/api/checkout/billing-address`) which signs the call server-side
 * with the App token. Same pattern as `checkoutRefresh`.
 */
type BillingAddressInput = CheckoutAddressPayload;

export async function checkoutBillingAddressUpdate(params: {
  id: string;
  address: BillingAddressInput;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  if (isCheckoutBrowserClient()) {
    return checkoutBillingAddressUpdateViaApi(params);
  }
  return checkoutBillingAddressUpdateDirect(params);
}

/**
 * Client-side billing-address update — proxies through the BFF so the App
 * token (which lives only in server env) is the one that authenticates with
 * Saleor.
 */
async function checkoutBillingAddressUpdateViaApi(params: {
  id: string;
  address: BillingAddressInput;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  return postCheckoutBff(
    '/api/checkout/billing-address',
    {
      checkoutId: params.id,
      address: params.address,
      languageCode: params.languageCode,
    },
    { code: 'ADDRESS_UPDATE_FAILED', message: 'Failed to update billing address' },
    '[Checkout] Failed to update billing address via API:'
  );
}

/**
 * Server-side billing-address update — direct Saleor call using the App token.
 */
async function checkoutBillingAddressUpdateDirect(params: {
  id: string;
  address: BillingAddressInput;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  try {
    const languageCode = (params.languageCode?.toUpperCase().replace('-', '_') ||
      DEFAULT_LANGUAGE_CODE) as SaleorLanguageCodeEnum;

    const result = await saleorMutate(CheckoutBillingAddressUpdateDocument, {
      id: params.id,
      address: mapToSaleorAddress(params.address),
      languageCode,
      thumbnailSize: THUMBNAIL_SIZE,
      imagesFormat: IMAGES_FORMAT,
    });

    const errors = result.checkoutBillingAddressUpdate?.errors?.map(mapApiError) ?? [];
    const checkout = result.checkoutBillingAddressUpdate?.checkout
      ? mapCheckoutToData(result.checkoutBillingAddressUpdate.checkout)
      : null;

    if (errors.length > 0) {
      devError(
        '[Checkout] Saleor billing address update returned errors:',
        JSON.stringify(errors, null, 2)
      );
    }

    return { checkout, errors };
  } catch (error) {
    devError('[Checkout] Failed to update billing address:', error);
    return {
      checkout: null,
      errors: [
        {
          code: 'ADDRESS_UPDATE_FAILED',
          message: 'Failed to update address',
        },
      ],
    };
  }
}

/**
 * Attach a customer to an anonymous checkout
 *
 * This associates the checkout with the currently authenticated user.
 * Used during login to persist the anonymous cart to the user's account.
 *
 * IMPORTANT: This mutation requires user authentication.
 * The user's access token must be passed via the Authorization header.
 *
 * @param id - Checkout ID to attach
 * @param accessToken - User's access token for authentication
 * @param languageCode - Language code for translations
 */
export async function checkoutCustomerAttach(params: {
  id: string;
  accessToken: string;
  languageCode?: string;
}): Promise<{
  checkout: CheckoutData | null;
  errors: ApiError[];
}> {
  try {
    const languageCode = (params.languageCode?.toUpperCase().replace('-', '_') ||
      DEFAULT_LANGUAGE_CODE) as SaleorLanguageCodeEnum;

    // Use user token for this mutation (requires authenticated user)
    const result = await saleorMutate(
      CheckoutCustomerAttachDocument,
      {
        id: params.id,
        languageCode,
        thumbnailSize: THUMBNAIL_SIZE,
        imagesFormat: IMAGES_FORMAT,
      },
      params.accessToken
    );

    const errors = result.checkoutCustomerAttach?.errors?.map(mapApiError) ?? [];
    const checkout = result.checkoutCustomerAttach?.checkout
      ? mapCheckoutToData(result.checkoutCustomerAttach.checkout)
      : null;

    if (errors.length > 0) {
      devError('[Checkout] Customer attach errors:', errors);
    }

    return { checkout, errors };
  } catch (error) {
    devError('[Checkout] Failed to attach customer:', error);
    return {
      checkout: null,
      errors: [
        {
          code: 'CUSTOMER_ATTACH_FAILED',
          message: error instanceof Error ? error.message : 'Failed to attach customer to checkout',
        },
      ],
    };
  }
}

/**
 * Update an order via Saleor `orderUpdate` mutation.
 *
 * Uses the server-side `SALEOR_APP_TOKEN` (via `saleorMutate`). Called from the
 * express checkout BFF when PayPal shipping or customer details change after the
 * draft order has been created.
 *
 * @param variables - Saleor mutation variables: `id` or `externalReference` plus
 *   `input` fields such as `billingAddress`, `shippingAddress`, or `userEmail`
 * @returns Updated order fragment on success, or mapped Saleor field errors.
 *   Network/unexpected failures return `order: null` with `ORDER_UPDATE_FAILED`.
 *
 * @see app/api/checkout/express/update-saleor-order/route.ts
 * @see lib/checkout/operations/updateSaleorOrderClient.ts
 */
export async function orderUpdate(variables: SaleorOrderUpdateMutationVariables): Promise<{
  order: SaleorOrderFragment | null;
  errors: ApiError[];
}> {
  try {
    const result = await saleorMutate(OrderUpdateDocument, variables);

    const errors = result.orderUpdate?.errors?.map(mapApiError) ?? [];
    const order = result.orderUpdate?.order ?? null;

    if (errors.length > 0) {
      devError('[Checkout] Order update errors:', errors);
    }

    return { order, errors };
  } catch (error) {
    devError('[Checkout] Failed to update order:', error);
    return {
      order: null,
      errors: [
        {
          code: 'ORDER_UPDATE_FAILED',
          message: error instanceof Error ? error.message : 'Failed to update order',
        },
      ],
    };
  }
}
